import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ShoppingCart,
  Minus,
  Plus,
  ShieldCheck,
  Zap,
  Award,
  ArrowLeft,
  Star
} from 'lucide-react';
import api from '../api';
import { useCart } from '../CartContext';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  const [reviews, setReviews] = useState([]);
  const [ratingStats, setRatingStats] = useState({
    avg_rating: 0,
    total_reviews: 0
  });

  const [selectedRating, setSelectedRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProduct();
    fetchReviews();
    fetchRatingStats();
    window.scrollTo(0, 0);
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await api.get(`/products/${id}/reviews`);
      setReviews(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      setReviews([]);
    }
  };

  const fetchRatingStats = async () => {
    try {
      const response = await api.get(`/products/${id}/rating`);

      setRatingStats({
        avg_rating: Number(response.data.avg_rating || 0),
        total_reviews: Number(response.data.total_reviews || 0)
      });
    } catch (error) {
      console.error('Failed to fetch rating stats:', error);

      setRatingStats({
        avg_rating: 0,
        total_reviews: 0
      });
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }
    try {
      await addToCart(product.id, quantity);
      toast.success(`Added ${quantity} item(s) to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const handleSubmitReview = async (event) => {
    event.preventDefault();

    if (!user) {
      toast.error('Please login to submit a review');
      navigate('/login');
      return;
    }

    if (!reviewText.trim()) {
      toast.error('Please write your review');
      return;
    }

    try {
      setReviewLoading(true);

      await api.post(`/products/${id}/reviews`, {
        rating: selectedRating,
        review: reviewText.trim()
      });

      toast.success('Review submitted successfully');

      setSelectedRating(5);
      setReviewText('');

      await Promise.all([
        fetchReviews(),
        fetchRatingStats()
      ]);
    } catch (error) {
      console.error('Review submission failed:', error);

      toast.error(
        error.response?.data?.detail ||
        'Failed to submit review'
      );
    } finally {
      setReviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#fcfcfc]">
        <div className="w-16 h-16 border-4 border-[#17847c]/20 border-t-[#17847c] rounded-full animate-spin"></div>
        <p className="mt-4 text-xs font-black tracking-[0.3em] text-gray-400 uppercase">Loading Elite Item...</p>
      </div>
    );
  }

  if (!product) return null;



  return (
    <div className="min-h-screen bg-[#fcfcfc] py-20 relative overflow-hidden" data-testid="product-detail-page">
      {/* Aesthetic Background Decor */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#17847c]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">

        {/* Back Button */}
        <button onClick={() => navigate('/products')} className="group mb-12 flex items-center gap-2 text-xs font-black tracking-widest text-gray-400 hover:text-[#17847c] transition-all">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          BACK TO COLLECTION
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Product Image - Luxury Frame */}
          <div className="animate-in fade-in slide-in-from-left-10 duration-1000">
            <div className="aspect-square bg-white rounded-[3rem] overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.1)] border border-gray-100 group">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110"
                data-testid="product-detail-image"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="animate-in fade-in slide-in-from-right-10 duration-1000 space-y-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="bg-[#17847c]/10 text-[#17847c] text-[10px] font-black px-3 py-1 rounded-full tracking-widest uppercase">Professional Choice</span>
                <span className="text-gray-300 text-xs">•</span>
                <span className="text-gray-400 text-xs font-bold uppercase tracking-widest">{product.category}</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-black text-gray-900 tracking-tighter leading-[0.9]" data-testid="product-detail-name">
                {product.name.split(' ')[0]} <br />
                <span className="text-[#17847c] italic">{product.name.split(' ').slice(1).join(' ')}</span>
              </h1>
              <p className="text-4xl font-black text-gray-900 tracking-tighter" data-testid="product-detail-price">
                ₹{Number(product.price || 0).toFixed(2)}
              </p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${star <= Math.round(ratingStats.avg_rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                        }`}
                    />
                  ))}
                </div>

                <span className="text-sm font-black text-gray-700">
                  {ratingStats.avg_rating.toFixed(1)}
                </span>

                <span className="text-sm font-bold text-gray-400">
                  ({ratingStats.total_reviews} reviews)
                </span>
              </div>
            </div>

            <p className="text-xl text-gray-500 font-light leading-relaxed max-w-xl">
              {product.description}
            </p>

            {product.features && product.features.length > 0 && (
              <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
                <h3 className="text-xs font-black tracking-[0.25em] text-gray-400 uppercase mb-4">
                  Product Features
                </h3>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3 text-sm font-bold text-gray-600">
                      <span className="w-6 h-6 rounded-full bg-[#17847c]/10 text-[#17847c] flex items-center justify-center text-xs">
                        ✓
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Key Features Icons */}
            <div className="grid grid-cols-3 gap-4 pt-4">
              <div className="p-4 bg-white border border-gray-100 rounded-2xl text-center space-y-2">
                <ShieldCheck className="w-6 h-6 text-[#17847c] mx-auto" />
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">Skin Safe</p>
              </div>
              <div className="p-4 bg-white border border-gray-100 rounded-2xl text-center space-y-2">
                <Zap className="w-6 h-6 text-[#17847c] mx-auto" />
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">Extra Strong</p>
              </div>
              <div className="p-4 bg-white border border-gray-100 rounded-2xl text-center space-y-2">
                <Award className="w-6 h-6 text-[#17847c] mx-auto" />
                <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">Elite Grade</p>
              </div>
            </div>

            {/* Selection Area */}
            <div className="space-y-6 pt-8 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <label className="text-xs font-black tracking-widest text-gray-400 uppercase">Quantity</label>
                <div className="flex items-center bg-gray-50 rounded-2xl p-1 border border-gray-100">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-12 h-12 flex items-center justify-center hover:bg-white rounded-xl transition-all"><Minus className="w-4 h-4" /></button>
                  <span className="w-12 text-center font-black text-xl">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="w-12 h-12 flex items-center justify-center hover:bg-white rounded-xl transition-all"><Plus className="w-4 h-4" /></button>
                </div>
              </div>

              <Button
                onClick={handleAddToCart}
                className="w-full h-20 bg-[#17847c] hover:bg-black text-white rounded-3xl text-lg font-black tracking-[0.2em] shadow-2xl shadow-[#17847c]/20 transition-all flex items-center justify-center gap-4"
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="w-6 h-6" />
                ADD TO BAG
              </Button>
            </div>

            {/* Inventory Status */}
            <div className="flex items-center justify-between text-[10px] font-black tracking-[0.2em] uppercase text-gray-400">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${product.stock > 0 ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                {product.stock > 0 ? `${product.stock} Units in stock` : 'Out of stock'}
              </div>
              <span>SKU: WZ-{product.id.substring(0, 6)}</span>
            </div>
          </div>
        </div>
        {/* Customer Reviews Section */}
        <section className="mt-24 border-t border-gray-100 pt-16">

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* Rating Summary */}
            <div className="bg-white rounded-[2rem] border border-gray-100 p-8 shadow-sm h-fit">

              <p className="text-xs font-black tracking-[0.25em] uppercase text-gray-400 mb-4">
                Customer Rating
              </p>

              <div className="flex items-end gap-3 mb-4">
                <span className="text-6xl font-black text-gray-900">
                  {ratingStats.avg_rating.toFixed(1)}
                </span>

                <span className="text-gray-400 font-bold mb-2">
                  / 5
                </span>
              </div>

              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-6 h-6 ${star <= Math.round(ratingStats.avg_rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                      }`}
                  />
                ))}
              </div>

              <p className="text-sm font-bold text-gray-400">
                Based on {ratingStats.total_reviews} customer reviews
              </p>
            </div>

            {/* Review Form and Review List */}
            <div className="lg:col-span-2 space-y-10">

              {/* Review Form */}
              <div className="bg-white rounded-[2rem] border border-gray-100 p-8 shadow-sm">

                <h2 className="text-3xl font-black text-gray-900 tracking-tight mb-2">
                  Write a Review
                </h2>

                <p className="text-sm text-gray-400 mb-8">
                  Share your experience with this product.
                </p>

                {!user ? (
                  <div className="bg-gray-50 rounded-2xl p-6 text-center">
                    <p className="text-gray-500 font-bold mb-4">
                      Please login to submit a review.
                    </p>

                    <Button
                      onClick={() => navigate('/login')}
                      className="bg-[#17847c] hover:bg-black text-white rounded-xl"
                    >
                      Login to Review
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitReview} className="space-y-6">

                    <div>
                      <label className="block text-xs font-black tracking-widest uppercase text-gray-400 mb-3">
                        Your Rating
                      </label>

                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setSelectedRating(star)}
                            className="transition-transform hover:scale-110"
                          >
                            <Star
                              className={`w-8 h-8 ${star <= selectedRating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300'
                                }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="reviewText"
                        className="block text-xs font-black tracking-widest uppercase text-gray-400 mb-3"
                      >
                        Your Review
                      </label>

                      <textarea
                        id="reviewText"
                        value={reviewText}
                        onChange={(event) => setReviewText(event.target.value)}
                        rows={5}
                        maxLength={1000}
                        placeholder="Tell us what you liked about this product..."
                        className="w-full rounded-2xl border border-gray-200 px-5 py-4 text-gray-700 outline-none focus:border-[#17847c] focus:ring-2 focus:ring-[#17847c]/10 resize-none"
                      />

                      <div className="text-right mt-2 text-xs font-bold text-gray-400">
                        {reviewText.length}/1000
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={reviewLoading}
                      className="bg-[#17847c] hover:bg-black text-white rounded-2xl px-8 h-12 font-black tracking-wider"
                    >
                      {reviewLoading ? 'SUBMITTING...' : 'SUBMIT REVIEW'}
                    </Button>

                  </form>
                )}
              </div>

              {/* Reviews List */}
              <div>
                <h2 className="text-3xl font-black text-gray-900 tracking-tight mb-8">
                  Customer Reviews
                </h2>

                {reviews.length === 0 ? (
                  <div className="bg-white rounded-[2rem] border border-gray-100 p-10 text-center shadow-sm">
                    <Star className="w-12 h-12 text-gray-200 mx-auto mb-4" />

                    <p className="font-black text-gray-700">
                      No reviews yet
                    </p>

                    <p className="text-sm text-gray-400 mt-2">
                      Be the first customer to review this product.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {reviews.map((item) => (
                      <article
                        key={item.id}
                        className="bg-white rounded-[2rem] border border-gray-100 p-7 shadow-sm"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">

                          <div>
                            <h3 className="font-black text-gray-900">
                              {item.user_name}
                            </h3>

                            <p className="text-xs font-bold text-gray-400 mt-1">
                              {new Date(item.created_at).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </p>
                          </div>

                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-5 h-5 ${star <= Number(item.rating)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                  }`}
                              />
                            ))}
                          </div>
                        </div>

                        <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                          {item.review}
                        </p>
                      </article>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
